import {
  DeesElement,
  css,
  cssManager,
  customElement,
  html,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';

export type TDeesPopoverPlacement = 'top' | 'bottom';
export type TDeesPopoverCloseReason = 'explicit' | 'escape' | 'outside' | 'tab' | 'anchor-hidden';

export interface IDeesPopoverCloseDetail {
  reason: TDeesPopoverCloseReason;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-popover': DeesPopover;
  }
}

interface IAnchorAriaSnapshot {
  element: HTMLElement;
  expanded: string | null;
  hasPopup: string | null;
  controls: string | null;
}

interface IRectEdges {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

let nextPopoverId = 0;

/**
 * Controlled, nonmodal rich-content popover anchored to an HTMLElement.
 * Geometry, viewport clipping, dismissal, and focus return are owned here so
 * consumers only provide an anchor and slotted content.
 */
@customElement('dees-popover')
export class DeesPopover extends DeesElement {
  @property({ attribute: false })
  accessor anchor: HTMLElement | null = null;

  @property({ type: Boolean, reflect: true })
  accessor open: boolean = false;

  @property()
  accessor label: string = 'Details';

  @property({ reflect: true })
  accessor placement: TDeesPopoverPlacement = 'top';

  @property({ type: Boolean, reflect: true })
  accessor showTail: boolean = true;

  /** Close on the first Tab. Disable for panels containing multiple controls. */
  @property({ type: Boolean })
  accessor dismissOnTab: boolean = true;

  @property()
  accessor width: string = 'min(320px, calc(100vw - 16px))';

  @property()
  accessor maxHeight: string = 'min(60vh, 360px)';

  private openListeners: AbortController | undefined;
  private resizeObserver: ResizeObserver | undefined;
  private positionFrame: number | undefined;
  private anchorAriaSnapshot: IAnchorAriaSnapshot | undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        position: fixed;
        inset: auto;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--dees-color-text-primary);
        overflow: visible;
      }

      :host(:not(:popover-open)) {
        display: none;
      }

      .surface {
        box-sizing: border-box;
        width: 100%;
        max-height: inherit;
        overflow: auto;
        overscroll-behavior: contain;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-material-regular-bg-opaque);
        box-shadow: var(--dees-shadow-lg);
      }

      .tail {
        position: absolute;
        left: var(--dees-popover-tail-left, 50%);
        width: 10px;
        height: 10px;
        border: solid var(--dees-color-border-subtle);
        background: var(--dees-material-regular-bg-opaque);
        transform: translateX(-50%) rotate(45deg);
        pointer-events: none;
      }

      :host([actual-placement='top']) .tail {
        bottom: -5px;
        border-width: 0 1px 1px 0;
      }

      :host([actual-placement='bottom']) .tail {
        top: -5px;
        border-width: 1px 0 0 1px;
      }

    `,
  ];

  public async connectedCallback(): Promise<void> {
    this.id ||= `dees-popover-${++nextPopoverId}`;
    this.popover = 'manual';
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'false');
    await super.connectedCallback();
    this.syncOpenState();
  }

  public updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    this.setAttribute('aria-label', this.label);
    if (changedProperties.has('anchor')) this.restoreAnchorAria();
    if (
      changedProperties.has('open')
      || changedProperties.has('anchor')
      || changedProperties.has('placement')
      || changedProperties.has('width')
      || changedProperties.has('maxHeight')
      || changedProperties.has('showTail')
    ) {
      this.syncOpenState();
    }
  }

  public async disconnectedCallback(): Promise<void> {
    this.teardownOpenState();
    this.restoreAnchorAria();
    await super.disconnectedCallback();
  }

  public render(): TemplateResult {
    return html`
      <div class="surface"><slot></slot></div>
      ${this.showTail ? html`<span class="tail" aria-hidden="true"></span>` : ''}
    `;
  }

  /** Close from an in-popover control and return focus to the current anchor. */
  public close(): void {
    this.requestClose('explicit', true);
  }

  /** Focus an explicitly autofocused or first interactive slotted element. */
  public async focusFirst(): Promise<void> {
    await this.updateComplete;
    const candidate = this.querySelector<HTMLElement>(
      '[autofocus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    candidate?.focus();
  }

  private syncOpenState(): void {
    if (!this.isConnected) return;
    if (!this.open) {
      this.teardownOpenState();
      return;
    }
    if (!this.anchor || !this.anchorIsVisible()) {
      this.requestClose('anchor-hidden', false);
      return;
    }

    this.applyAnchorAria();
    if (!this.matches(':popover-open')) {
      this.style.visibility = 'hidden';
      this.showPopover();
    }
    this.style.width = this.width;
    this.style.maxHeight = this.maxHeight;
    this.installOpenListeners();
    this.schedulePosition();
  }

  private installOpenListeners(): void {
    this.openListeners?.abort();
    this.openListeners = new AbortController();
    const { signal } = this.openListeners;
    document.addEventListener('keydown', this.handleDocumentKeydown, { capture: true, signal });
    document.addEventListener('pointerdown', this.handleDocumentPointerdown, { capture: true, signal });
    document.addEventListener('focusin', this.handleDocumentFocusin, { capture: true, signal });
    document.addEventListener('scroll', this.handleViewportChange, { capture: true, signal });
    window.addEventListener('resize', this.handleViewportChange, { signal });
    window.addEventListener('blur', this.handleWindowBlur, { signal });
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.schedulePosition());
    if (this.anchor) this.resizeObserver.observe(this.anchor);
    this.resizeObserver.observe(this);
  }

  private teardownOpenState(): void {
    this.openListeners?.abort();
    this.openListeners = undefined;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (this.positionFrame !== undefined) cancelAnimationFrame(this.positionFrame);
    this.positionFrame = undefined;
    if (this.matches(':popover-open')) this.hidePopover();
    this.style.visibility = '';
    this.restoreAnchorAria();
  }

  private handleDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.requestClose('escape', true);
    } else if (event.key === 'Tab' && this.dismissOnTab) {
      this.requestClose('tab', false);
    }
  };

  private handleDocumentPointerdown = (event: PointerEvent): void => {
    const path = event.composedPath();
    if (path.includes(this) || (this.anchor && path.includes(this.anchor))) return;
    this.requestClose('outside', false);
  };

  private handleDocumentFocusin = (event: FocusEvent): void => {
    if (this.dismissOnTab) return;
    const path = event.composedPath();
    if (path.includes(this) || (this.anchor && path.includes(this.anchor))) return;
    this.requestClose('tab', false);
  };

  private handleViewportChange = (): void => {
    if (!this.anchorIsVisible()) {
      this.requestClose('anchor-hidden', false);
      return;
    }
    this.schedulePosition();
  };

  private handleWindowBlur = (): void => {
    this.requestClose('outside', false);
  };

  private requestClose(reason: TDeesPopoverCloseReason, restoreFocus: boolean): void {
    if (!this.open) return;
    const anchor = this.anchor;
    this.open = false;
    this.teardownOpenState();
    this.dispatchEvent(new CustomEvent<IDeesPopoverCloseDetail>('dees-popover-close', {
      detail: { reason },
      bubbles: true,
      composed: true,
    }));
    if (restoreFocus && anchor?.isConnected) anchor.focus();
  }

  private schedulePosition(): void {
    if (this.positionFrame !== undefined) return;
    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = undefined;
      this.position();
    });
  }

  private position(): void {
    if (!this.open || !this.anchor || !this.matches(':popover-open')) return;
    if (!this.anchorIsVisible()) {
      this.requestClose('anchor-hidden', false);
      return;
    }
    const gap = this.showTail ? 8 : 4;
    const anchorRect = this.anchor.getBoundingClientRect();
    const popoverRect = this.getBoundingClientRect();
    const left = Math.max(8, Math.min(
      anchorRect.left + (anchorRect.width - popoverRect.width) / 2,
      window.innerWidth - popoverRect.width - 8,
    ));
    const desiredTop = this.placement === 'top'
      ? anchorRect.top - popoverRect.height - gap
      : anchorRect.bottom + gap;
    const top = Math.max(8, Math.min(desiredTop, window.innerHeight - popoverRect.height - 8));
    this.style.left = `${left}px`;
    this.style.top = `${top}px`;
    this.style.setProperty(
      '--dees-popover-tail-left',
      `${Math.max(8, Math.min(popoverRect.width - 8, anchorRect.left + anchorRect.width / 2 - left))}px`,
    );
    this.setAttribute('actual-placement', this.placement);
    this.style.visibility = '';
  }

  private anchorIsVisible(): boolean {
    const anchor = this.anchor;
    if (!anchor?.isConnected || !anchor.getClientRects().length) return false;
    let visibleRect: IRectEdges = anchor.getBoundingClientRect();
    visibleRect = intersectRects(visibleRect, {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    });
    if (visibleRect.right <= visibleRect.left || visibleRect.bottom <= visibleRect.top) return false;

    let ancestor = parentElementAcrossShadow(anchor);
    while (ancestor) {
      const style = getComputedStyle(ancestor);
      if (/(auto|scroll|hidden|clip)/.test(`${style.overflow} ${style.overflowX} ${style.overflowY}`)) {
        visibleRect = intersectRects(visibleRect, ancestor.getBoundingClientRect());
        if (visibleRect.right <= visibleRect.left || visibleRect.bottom <= visibleRect.top) return false;
      }
      ancestor = parentElementAcrossShadow(ancestor);
    }
    return true;
  }

  private applyAnchorAria(): void {
    const anchor = this.anchor;
    if (!anchor) return;
    if (this.anchorAriaSnapshot?.element !== anchor) {
      this.restoreAnchorAria();
      this.anchorAriaSnapshot = {
        element: anchor,
        expanded: anchor.getAttribute('aria-expanded'),
        hasPopup: anchor.getAttribute('aria-haspopup'),
        controls: anchor.getAttribute('aria-controls'),
      };
    }
    anchor.setAttribute('aria-expanded', 'true');
    anchor.setAttribute('aria-haspopup', 'dialog');
    const controls = new Set((this.anchorAriaSnapshot.controls ?? '').split(/\s+/).filter(Boolean));
    controls.add(this.id);
    anchor.setAttribute('aria-controls', [...controls].join(' '));
  }

  private restoreAnchorAria(): void {
    const snapshot = this.anchorAriaSnapshot;
    if (!snapshot) return;
    restoreAttribute(snapshot.element, 'aria-expanded', snapshot.expanded);
    restoreAttribute(snapshot.element, 'aria-haspopup', snapshot.hasPopup);
    restoreAttribute(snapshot.element, 'aria-controls', snapshot.controls);
    this.anchorAriaSnapshot = undefined;
  }
}

const restoreAttribute = (element: HTMLElement, name: string, value: string | null): void => {
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
};

const parentElementAcrossShadow = (element: Element): HTMLElement | null => {
  if (element.parentElement) return element.parentElement;
  const root = element.getRootNode();
  return root instanceof ShadowRoot && root.host instanceof HTMLElement ? root.host : null;
};

const intersectRects = (
  first: IRectEdges,
  second: IRectEdges,
) => ({
  left: Math.max(first.left, second.left),
  top: Math.max(first.top, second.top),
  right: Math.min(first.right, second.right),
  bottom: Math.min(first.bottom, second.bottom),
});
